package org.example.project

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.withTimeoutOrNull

enum class AuthState {
    LOADING,
    LOGGED_IN,
    LOGGED_OUT
}

object FirebaseManager {

    private val _currentUserDoc = MutableStateFlow<UserDocument?>(null)
    val currentUserDoc: StateFlow<UserDocument?> = _currentUserDoc.asStateFlow()

    private val _currentSystemProfile = MutableStateFlow<SystemProfileDocument?>(null)
    val currentSystemProfile: StateFlow<SystemProfileDocument?> = _currentSystemProfile.asStateFlow()

    private val _authState = MutableStateFlow(AuthState.LOADING)
    val authState: StateFlow<AuthState> = _authState.asStateFlow()

    private var userDocUnsubscribe: (() -> Unit)? = null
    private var systemProfileUnsubscribe: (() -> Unit)? = null

    fun initialize(config: FirebaseConfig) {
        org.example.project.initializeFirebase(config)
        // Start listening to auth state immediately after initialization
        org.example.project.listenToAuthState { isLoggedIn ->
            _authState.value = if (isLoggedIn) AuthState.LOGGED_IN else AuthState.LOGGED_OUT
            if (isLoggedIn) {
                val uid = org.example.project.getCurrentUserUid()
                if (uid != null) {
                    userDocUnsubscribe?.invoke()
                    userDocUnsubscribe = org.example.project.listenToUserDocument(uid) { doc ->
                        _currentUserDoc.value = doc
                    }

                    systemProfileUnsubscribe?.invoke()
                    systemProfileUnsubscribe = org.example.project.listenToSystemProfileDocument(uid) { doc ->
                        _currentSystemProfile.value = doc
                    }
                }
            } else {
                userDocUnsubscribe?.invoke()
                userDocUnsubscribe = null
                _currentUserDoc.value = null

                systemProfileUnsubscribe?.invoke()
                systemProfileUnsubscribe = null
                _currentSystemProfile.value = null
            }
        }
    }

    private suspend fun checkLoginPermission(): String? {
        // Wait for user doc to be loaded from Firestore (max 5 seconds)
        val doc = withTimeoutOrNull(5000) {
            currentSystemProfile.filterNotNull().first()
        }
        
        if (doc == null) {
            signOut()
            return "Failed to verify account permissions. Please try again."
        }
        
        if (doc.role == UserRoleType.USER) {
            signOut()
            return "Access denied: This portal is for Administrators and Organizations only."
        }
        
        return null // Permission granted
    }

    suspend fun signInWithGoogle(): String? {
        val success = org.example.project.signInWithGoogle()
        if (!success) return "Google Sign In failed."
        syncWithBackend()
        return checkLoginPermission()
    }

    suspend fun signInWithApple(): String? {
        val success = org.example.project.signInWithApple()
        if (!success) return "Apple Sign In failed."
        syncWithBackend()
        return checkLoginPermission()
    }

    suspend fun signInWithEmail(email: String, pass: String): String? {
        val error = org.example.project.signInWithEmail(email, pass)
        if (error != null) return error
        syncWithBackend()
        return checkLoginPermission()
    }

    suspend fun signOut() {
        org.example.project.signOut()
        _currentUserDoc.value = null
    }

    fun getCurrentUserDisplayName(): String? {
        return org.example.project.getCurrentUserDisplayName()
    }

    fun getCurrentUserEmail(): String? {
        return org.example.project.getCurrentUserEmail()
    }

    fun getCurrentUserUid(): String? {
        return org.example.project.getCurrentUserUid()
    }

    fun getUserPoints(): Int {
        val balances = currentUserDoc.value?.balances
        return (balances?.iap_units ?: 0) + (balances?.bonus_units ?: 0)
    }

    suspend fun getCurrentUserIdToken(): String? {
        return org.example.project.getCurrentUserIdToken()
    }

    suspend fun userCheck(targetEmail: String): UserCheckResult {
        return BackendService.userCheck(targetEmail)
    }

    suspend fun getOrgInfo(targetEmail: String): GetOrgInfoResult {
        return BackendService.getOrgInfo(targetEmail)
    }

    suspend fun checkTaxInfo(targetEmail: String): CheckTaxInfoResult {
        return BackendService.checkTaxInfo(targetEmail)
    }

    suspend fun updateTaxInfo(targetEmail: String, taxCountry: String, taxId: String): UpdateTaxInfoResult {
        return BackendService.updateTaxInfo(targetEmail, taxCountry, taxId)
    }

    suspend fun enableOrg(email: String): EnableOrgResult {
        return BackendService.enableOrg(email)
    }

    suspend fun orgProvision(
        targetEmail: String, 
        amount: Int,
        settleFiatMethod: String? = null,
        settleFiatProof: String? = null,
        settleFiatAmount: Int? = null,
        settleTaxCountry: String? = null,
        settleTaxId: String? = null,
        settleTaxRate: Int? = null,
        settleRemark: String? = null
    ): OrgProvisionResult {
        return BackendService.orgProvision(
            targetEmail, 
            amount,
            settleFiatMethod,
            settleFiatProof,
            settleFiatAmount,
            settleTaxCountry,
            settleTaxId,
            settleTaxRate,
            settleRemark
        )
    }

    suspend fun orgReclaim(targetEmail: String, amount: Int): OrgReclaimResult {
        return BackendService.orgReclaim(targetEmail, amount)
    }

    suspend fun syncWithBackend(): SyncWithBackendResult {
        return BackendService.syncWithBackend()
    }

    suspend fun createLicenseKeys(bonusUnits: Int, count: Int): CreateLicenseKeyResult {
        return BackendService.createLicenseKeys(bonusUnits, count)
    }

    suspend fun listLicenseKeys(limit: Int = 20, status: LicenseKeyStatus? = null, cursor: String? = null): LicenseKeyListResult {
        return BackendService.listLicenseKeys(limit, status, cursor)
    }

    suspend fun getLicensePackages(): LicensePackageListResult {
        return BackendService.getLicensePackages()
    }

    suspend fun getIapPackages(): GetIapPackagesResult {
        return BackendService.getIapPackages()
    }

    suspend fun getOrgLevels(): OrgLevelListResult {
        return BackendService.getOrgLevels()
    }

    suspend fun checkLicenseKey(codeSn: String): CheckLicenseKeyResult {
        return BackendService.checkLicenseKey(codeSn)
    }

    suspend fun useLicenseKey(codeSn: String, codePin: String): UseLicenseKeyResult {
        return BackendService.useLicenseKey(codeSn, codePin)
    }

    suspend fun revokeLicenseKey(codeId: String, codeSn: String): RevokeLicenseKeyResult {
        return BackendService.revokeLicenseKey(codeId, codeSn)
    }

    suspend fun getTaxCountries(): GetTaxCountriesResult {
        return BackendService.getTaxCountries()
    }

    suspend fun getFiatPaymentMethods(): GetFiatPaymentMethodsResult {
        return BackendService.getFiatPaymentMethods()
    }
}